<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import assistantAvatar from '@/assets/customer-service/assistant-dog.png'
import CustomerServicePanel from './CustomerServicePanel.vue'

const route = useRoute()
const visible = ref(false)
const launcherRef = ref(null)
const isDocked = ref(true)
const isDragging = ref(false)
const suppressClick = ref(false)

const STORAGE_KEY = 'wms-customer-service-launcher-v2'
const VIEWPORT_PADDING = 16
const DOCK_THRESHOLD = 88
const FLOAT_SIZE = { width: 150, height: 56 }
const DOCKED_SIZE = { width: 96, height: 122 }

const launcherPosition = reactive({
  x: 0,
  y: 0,
})

const dragState = reactive({
  active: false,
  pointerId: -1,
  offsetX: 0,
  offsetY: 0,
  startX: 0,
  startY: 0,
  moved: false,
})

function normalizeRouteValue(value) {
  if (Array.isArray(value)) {
    return String(value[0] || '').trim()
  }

  return String(value || '').trim()
}

function pickRouteQueryValue(query, keys) {
  for (const key of keys) {
    const value = normalizeRouteValue(query?.[key])
    if (value) {
      return value
    }
  }

  return ''
}

const context = computed(() => ({
  currentModule: route.meta?.title || '系统页面',
  currentPath: route.fullPath || '',
  businessCode:
    pickRouteQueryValue(route.query, ['businessCode', 'business_code', 'code', 'no', 'billNo']) ||
    String(route.name || '').trim(),
  recordId:
    pickRouteQueryValue(route.query, ['id', 'recordId', 'record_id', 'billId']) ||
    normalizeRouteValue(route.params?.id),
  recordTitle:
    pickRouteQueryValue(route.query, ['title', 'name', 'recordTitle', 'record_title', 'billTitle', 'billName']) ||
    route.meta?.title ||
    '',
}))

const launcherStyle = computed(() => {
  if (isDocked.value) {
    return {
      top: `${launcherPosition.y}px`,
      right: '0px',
    }
  }

  return {
    top: `${launcherPosition.y}px`,
    left: `${launcherPosition.x}px`,
  }
})

const dockRailStyle = computed(() => ({
  top: `${Math.max(VIEWPORT_PADDING, launcherPosition.y - 8)}px`,
  opacity: isDocked.value || isDragging.value ? 1 : 0.5,
}))

function clamp(value, min, max) {
  if (max <= min) {
    return min
  }

  return Math.min(Math.max(value, min), max)
}

function getLauncherSize(docked = isDocked.value) {
  return docked ? DOCKED_SIZE : FLOAT_SIZE
}

function clampPosition(nextPosition, docked = isDocked.value) {
  if (typeof window === 'undefined') {
    return {
      x: nextPosition.x,
      y: nextPosition.y,
    }
  }

  const size = getLauncherSize(docked)

  return {
    x: clamp(nextPosition.x, VIEWPORT_PADDING, window.innerWidth - size.width - VIEWPORT_PADDING),
    y: clamp(nextPosition.y, VIEWPORT_PADDING, window.innerHeight - size.height - VIEWPORT_PADDING),
  }
}

function getDefaultLauncherState() {
  if (typeof window === 'undefined') {
    return {
      docked: true,
      x: 0,
      y: 120,
    }
  }

  return {
    docked: true,
    x: window.innerWidth - DOCKED_SIZE.width,
    y: clamp(
      window.innerHeight - DOCKED_SIZE.height - 92,
      120,
      window.innerHeight - DOCKED_SIZE.height - VIEWPORT_PADDING
    ),
  }
}

function persistLauncherState() {
  if (typeof window === 'undefined') {
    return
  }

  window.localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      docked: isDocked.value,
      x: launcherPosition.x,
      y: launcherPosition.y,
    })
  )
}

function restoreLauncherState() {
  if (typeof window === 'undefined') {
    return
  }

  const fallback = getDefaultLauncherState()
  const savedState = window.localStorage.getItem(STORAGE_KEY)

  if (!savedState) {
    isDocked.value = fallback.docked
    launcherPosition.x = fallback.x
    launcherPosition.y = fallback.y
    persistLauncherState()
    return
  }

  try {
    const parsed = JSON.parse(savedState)
    const docked = Boolean(parsed?.docked)
    const nextPosition = clampPosition(
      {
        x: Number(parsed?.x ?? fallback.x),
        y: Number(parsed?.y ?? fallback.y),
      },
      docked
    )

    isDocked.value = docked
    launcherPosition.x = nextPosition.x
    launcherPosition.y = nextPosition.y
  } catch {
    isDocked.value = fallback.docked
    launcherPosition.x = fallback.x
    launcherPosition.y = fallback.y
    persistLauncherState()
  }
}

function syncLauncherPosition(rawX, rawY) {
  if (typeof window === 'undefined') {
    return
  }

  const shouldDock = rawX + FLOAT_SIZE.width >= window.innerWidth - DOCK_THRESHOLD
  const nextPosition = clampPosition({ x: rawX, y: rawY }, shouldDock)

  isDocked.value = shouldDock
  launcherPosition.x = shouldDock ? window.innerWidth - DOCKED_SIZE.width : nextPosition.x
  launcherPosition.y = nextPosition.y
}

function handlePointerMove(event) {
  if (!dragState.active || dragState.pointerId !== event.pointerId) {
    return
  }

  const deltaX = event.clientX - dragState.startX
  const deltaY = event.clientY - dragState.startY

  if (!dragState.moved && Math.hypot(deltaX, deltaY) > 6) {
    dragState.moved = true
    isDragging.value = true
  }

  if (!dragState.moved) {
    return
  }

  const rawX = event.clientX - dragState.offsetX
  const rawY = event.clientY - dragState.offsetY
  syncLauncherPosition(rawX, rawY)
}

function cleanupDragListeners() {
  window.removeEventListener('pointermove', handlePointerMove)
  window.removeEventListener('pointerup', handlePointerUp)
  window.removeEventListener('pointercancel', handlePointerUp)
}

function handlePointerUp(event) {
  if (!dragState.active || dragState.pointerId !== event.pointerId) {
    return
  }

  suppressClick.value = dragState.moved
  dragState.active = false
  dragState.pointerId = -1
  dragState.moved = false
  isDragging.value = false
  cleanupDragListeners()
  persistLauncherState()

  if (suppressClick.value) {
    window.setTimeout(() => {
      suppressClick.value = false
    }, 0)
  }
}

function handlePointerDown(event) {
  if (event.button !== undefined && event.button !== 0) {
    return
  }

  const element = launcherRef.value
  if (!element) {
    return
  }

  const rect = element.getBoundingClientRect()
  dragState.active = true
  dragState.pointerId = event.pointerId
  dragState.offsetX = event.clientX - rect.left
  dragState.offsetY = event.clientY - rect.top
  dragState.startX = event.clientX
  dragState.startY = event.clientY
  dragState.moved = false

  window.addEventListener('pointermove', handlePointerMove)
  window.addEventListener('pointerup', handlePointerUp)
  window.addEventListener('pointercancel', handlePointerUp)
}

function handleLauncherClick() {
  if (suppressClick.value) {
    suppressClick.value = false
    return
  }

  visible.value = !visible.value
}

function closePanel() {
  visible.value = false
}

function handleResize() {
  const nextPosition = clampPosition(
    {
      x: launcherPosition.x,
      y: launcherPosition.y,
    },
    isDocked.value
  )

  launcherPosition.x = isDocked.value && typeof window !== 'undefined'
    ? window.innerWidth - DOCKED_SIZE.width
    : nextPosition.x
  launcherPosition.y = nextPosition.y
  persistLauncherState()
}

onMounted(() => {
  restoreLauncherState()
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  cleanupDragListeners()
  window.removeEventListener('resize', handleResize)
})
</script>

<template>
  <div class="customer-service-launcher">
    <div
      class="customer-service-launcher__dock-rail"
      :class="{ 'is-active': isDocked || isDragging }"
      :style="dockRailStyle"
      aria-hidden="true"
    ></div>

    <div
      ref="launcherRef"
      class="customer-service-launcher__handle"
      :class="{
        'is-docked': isDocked,
        'is-dragging': isDragging,
      }"
      :style="launcherStyle"
      @pointerdown="handlePointerDown"
    >
      <button
        type="button"
        class="customer-service-launcher__button"
        :class="{ 'is-docked': isDocked }"
        @click="handleLauncherClick"
      >
        <template v-if="isDocked">
          <span class="customer-service-launcher__dog-scene" aria-hidden="true">
            <span class="customer-service-launcher__dog-shadow"></span>
            <span class="customer-service-launcher__dog-tail"></span>
            <span class="customer-service-launcher__dog-body"></span>
            <span class="customer-service-launcher__dog-paw customer-service-launcher__dog-paw--front"></span>
            <span class="customer-service-launcher__dog-paw customer-service-launcher__dog-paw--back"></span>
            <span class="customer-service-launcher__dog-head">
              <span class="customer-service-launcher__dog-ear customer-service-launcher__dog-ear--left"></span>
              <span class="customer-service-launcher__dog-ear customer-service-launcher__dog-ear--right"></span>
              <img
                class="customer-service-launcher__dog-avatar"
                :src="assistantAvatar"
                alt="智能客服"
              >
            </span>
          </span>
          <span class="customer-service-launcher__dock-label">
            <span class="customer-service-launcher__dock-title">智能客服</span>
          </span>
        </template>

        <template v-else>
          <span class="customer-service-launcher__icon">
            <img
              class="customer-service-launcher__icon-avatar"
              :src="assistantAvatar"
              alt="智能客服"
            >
          </span>
          <span class="customer-service-launcher__text">智能客服</span>
        </template>
      </button>
    </div>

    <CustomerServicePanel
      v-show="visible"
      :visible="visible"
      :context="context"
      @close="closePanel"
    />
  </div>
</template>

<style scoped lang="scss">
.customer-service-launcher__dock-rail {
  position: fixed;
  right: 0;
  width: 24px;
  height: 138px;
  border-radius: 18px 0 0 18px;
  background: linear-gradient(180deg, rgba(39, 82, 214, 0.2) 0%, rgba(111, 177, 255, 0.38) 100%);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.44);
  z-index: 2201;
  pointer-events: none;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.customer-service-launcher__dock-rail.is-active {
  transform: translateX(0);
}

.customer-service-launcher__handle {
  position: fixed;
  z-index: 2203;
  touch-action: none;
  user-select: none;
}

.customer-service-launcher__handle.is-dragging {
  transition: none;
}

.customer-service-launcher__button {
  position: relative;
  height: 56px;
  padding: 0 18px 0 10px;
  border: 0;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #ffffff;
  cursor: grab;
  background: linear-gradient(135deg, #244dcb 0%, #4772ff 62%, #6fb1ff 100%);
  box-shadow: 0 18px 36px rgba(33, 70, 178, 0.28);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.customer-service-launcher__button:hover {
  transform: translateY(-2px);
  box-shadow: 0 24px 42px rgba(33, 70, 178, 0.32);
}

.customer-service-launcher__handle.is-dragging .customer-service-launcher__button {
  cursor: grabbing;
  transform: scale(1.02);
}

.customer-service-launcher__button.is-docked {
  width: 96px;
  height: 122px;
  padding: 0;
  border-radius: 28px 0 0 28px;
  align-items: flex-start;
  justify-content: flex-start;
  background:
    radial-gradient(circle at 18% 20%, rgba(255, 255, 255, 0.48) 0, rgba(255, 255, 255, 0) 34%),
    linear-gradient(160deg, #2f5be0 0%, #4a7fff 58%, #7cbeff 100%);
  box-shadow:
    -16px 22px 44px rgba(28, 58, 154, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.customer-service-launcher__icon {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.22);
}

.customer-service-launcher__icon-avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.customer-service-launcher__text {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.customer-service-launcher__dog-scene {
  position: absolute;
  inset: 0;
  overflow: visible;
}

.customer-service-launcher__dog-shadow {
  position: absolute;
  left: 10px;
  bottom: 18px;
  width: 58px;
  height: 18px;
  border-radius: 50%;
  background: rgba(24, 50, 124, 0.18);
  filter: blur(7px);
}

.customer-service-launcher__dog-body {
  position: absolute;
  left: 12px;
  bottom: 24px;
  width: 52px;
  height: 44px;
  border-radius: 22px 18px 18px 22px;
  background: linear-gradient(145deg, #ffd8b6 0%, #f6b57d 45%, #e28a46 100%);
  box-shadow:
    inset 0 6px 10px rgba(255, 255, 255, 0.3),
    0 12px 20px rgba(150, 82, 36, 0.25);
  transform: rotate(-6deg);
}

.customer-service-launcher__dog-tail {
  position: absolute;
  left: 4px;
  bottom: 48px;
  width: 20px;
  height: 12px;
  border-radius: 999px;
  border: 5px solid #f0b47c;
  border-left-color: transparent;
  border-bottom-color: transparent;
  transform: rotate(-28deg);
  box-shadow: 0 4px 6px rgba(150, 82, 36, 0.12);
}

.customer-service-launcher__dog-paw {
  position: absolute;
  bottom: 18px;
  width: 15px;
  height: 28px;
  border-radius: 999px;
  background: linear-gradient(180deg, #ffd5b0 0%, #f0a363 100%);
  box-shadow: inset 0 4px 6px rgba(255, 255, 255, 0.24);
}

.customer-service-launcher__dog-paw--front {
  left: 32px;
  transform: rotate(8deg);
}

.customer-service-launcher__dog-paw--back {
  left: 50px;
  transform: rotate(-6deg);
}

.customer-service-launcher__dog-head {
  position: absolute;
  right: 14px;
  top: 10px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  box-shadow:
    0 12px 24px rgba(29, 60, 154, 0.26),
    inset 0 0 0 3px rgba(255, 255, 255, 0.9);
}

.customer-service-launcher__dog-ear {
  position: absolute;
  top: -4px;
  width: 18px;
  height: 22px;
  border-radius: 14px 14px 6px 6px;
  background: linear-gradient(180deg, #f2bd8f 0%, #d98d56 100%);
  z-index: -1;
}

.customer-service-launcher__dog-ear--left {
  left: 2px;
  transform: rotate(-18deg);
}

.customer-service-launcher__dog-ear--right {
  right: 2px;
  transform: rotate(18deg);
}

.customer-service-launcher__dog-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  object-fit: cover;
}

.customer-service-launcher__dock-label {
  position: absolute;
  right: 12px;
  bottom: 10px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  color: #ffffff;
  text-align: right;
}

.customer-service-launcher__dock-title {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

@media (max-width: 960px) {
  .customer-service-launcher__dock-rail {
    width: 18px;
    height: 124px;
  }

  .customer-service-launcher__button:not(.is-docked) {
    padding-right: 16px;
  }
}
</style>
